/* Several purchase orders received as one. Line ids are Odoo ids, unique
   across orders, so counts, boxes and the EAN map merge without prefixes. */
import { loadProgress, saveProgress, emptyProgress } from './store';

/** The orders of a receipt, a few at a time: a whole supplier's 68 must not hit Odoo at once. */
export async function fetchOrders(poIds, get, size = 6) {
  const out = [];
  for (let i = 0; i < poIds.length; i += size) out.push(...(await Promise.all(poIds.slice(i, i + size).map(get))));
  return out;
}

const rank = { open: 0, no_move: 1, done: 2 };
const SIZE_RANK = { huge: 3, big: 2, small: 1 };

export function combineOrders(orders) {
  const multi = orders.length > 1;
  const lines = orders
    .flatMap((o) => o.lines.map((l) => ({ ...l, po: { id: o.id, name: o.name, supplier: o.supplier } })))
    .sort((a, b) => rank[a.status] - rank[b.status] || Number(b.shipped_pending) - Number(a.shipped_pending) || (a.name || '').localeCompare(b.name || ''));
  const barcodes = {};
  orders.forEach((o) =>
    Object.entries(o.barcodes || {}).forEach(([code, entries]) => {
      barcodes[code] = [...(barcodes[code] || []), ...entries];
    })
  );
  const sum = (key) => orders.reduce((s, o) => s + Number(o.totals?.[key] || 0), 0);
  const suppliers = [...new Set(orders.map((o) => o.supplier).filter(Boolean))];
  const sizeTags = orders.map((o) => o.size_tag).filter(Boolean);
  // the reasons across the orders, the strongest level per reason
  const LEVEL = { big: 1, very: 2, exceptional: 3 };
  const reasons = {};
  orders.forEach((o) =>
    (o.size_tags || []).forEach((s) => {
      if (!reasons[s.key] || LEVEL[s.level] > LEVEL[reasons[s.key].level]) reasons[s.key] = s;
    })
  );
  return {
    ids: orders.map((o) => o.id),
    multi,
    orders,
    id: orders[0]?.id,
    name: multi ? `${orders.length} orders` : orders[0]?.name,
    names: orders.map((o) => o.name),
    supplier: suppliers.length === 1 ? suppliers[0] : suppliers.join(' · '),
    expected: orders.map((o) => o.expected).filter(Boolean).sort()[0] || null,
    pickings: [...new Set(orders.flatMap((o) => o.pickings || []))],
    packages: orders.reduce((s, o) => s + (o.packages || 0), 0),
    pallets: orders.reduce((s, o) => s + (o.pallets || 0), 0),
    size_tag: sizeTags.sort((a, b) => (SIZE_RANK[b] || 0) - (SIZE_RANK[a] || 0))[0] || null,
    size_tags: Object.values(reasons),
    lines,
    barcodes,
    totals: {
      lines: sum('lines'),
      open_lines: sum('open_lines'),
      to_receive: sum('to_receive'),
      cartons: sum('cartons'),
      shipped_lines: sum('shipped_lines'),
      volume_l: sum('volume_l'),
      weight_kg: sum('weight_kg'),
      unsized_lines: sum('unsized_lines'),
      unconfirmed_lines: sum('unconfirmed_lines'),
      fragile_lines: sum('fragile_lines'),
      pallet_lines: sum('pallet_lines'),
    },
  };
}

export function combineDestinations(list) {
  const lines = {};
  list.forEach((d) => Object.assign(lines, d?.lines || {}));
  return { lines };
}

/** Progress is stored per purchase order; merge for the screen, split to save. */
export function loadCombinedProgress(poIds) {
  const parts = poIds.map(loadProgress);
  return {
    counts: Object.assign({}, ...parts.map((p) => p.counts || {})),
    boxes: Object.assign({}, ...parts.map((p) => p.boxes || {})),
    scans: parts.flatMap((p) => p.scans || []),
    startedAt: parts.map((p) => p.startedAt).filter(Boolean).sort()[0] || null,
  };
}

export function saveCombinedProgress(combined, progress) {
  const poOfLine = {};
  combined.lines.forEach((l) => {
    poOfLine[l.id] = l.po.id;
  });
  combined.ids.forEach((poId) => {
    const counts = Object.fromEntries(Object.entries(progress.counts || {}).filter(([lineId]) => poOfLine[lineId] === poId));
    const boxes = Object.fromEntries(Object.entries(progress.boxes || {}).filter(([lineId]) => poOfLine[lineId] === poId));
    const scans = (progress.scans || []).filter((s) => (s.line_id ? poOfLine[s.line_id] === poId : poId === combined.ids[0]));
    saveProgress(poId, { ...emptyProgress(), counts, boxes, scans, startedAt: progress.startedAt });
  });
}
